'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LogoMark } from './LogoMark';
import { CopyEmailButton } from './interaction/CopyEmailButton';

const NAV_LINKS: { href: string; label: string }[] = [
  { href: '/', label: 'Home' },
  { href: '/projects', label: 'Projects' },
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Blog' },
  { href: '/services', label: 'Services' },
  { href: '/hire', label: 'For Hiring Teams' },
  { href: '/contact', label: 'Contact' },
];

function IconGitHub() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 .5C5.73.5.75 5.48.75 11.75c0 4.96 3.22 9.17 7.69 10.66.56.1.76-.24.76-.54v-1.9c-3.13.68-3.79-1.34-3.79-1.34-.51-1.3-1.25-1.64-1.25-1.64-1.02-.7.08-.68.08-.68 1.13.08 1.72 1.16 1.72 1.16 1 1.72 2.63 1.22 3.27.93.1-.73.39-1.22.71-1.5-2.5-.28-5.12-1.25-5.12-5.57 0-1.23.44-2.24 1.16-3.03-.12-.28-.5-1.43.11-2.98 0 0 .95-.3 3.1 1.16a10.8 10.8 0 0 1 5.64 0c2.15-1.46 3.1-1.16 3.1-1.16.61 1.55.23 2.7.11 2.98.72.79 1.16 1.8 1.16 3.03 0 4.33-2.63 5.28-5.14 5.56.4.34.76 1.02.76 2.06v3.05c0 .3.2.65.77.54A11.26 11.26 0 0 0 23.25 11.75C23.25 5.48 18.27.5 12 .5Z" />
    </svg>
  );
}

function IconMail() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function IconLinkedIn() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5.001 2.5 2.5 0 0 1 0-5.001ZM3 9h4v12H3V9Zm7 0h3.84v1.64h.06c.54-1 1.84-2.05 3.79-2.05 4.06 0 4.81 2.67 4.81 6.14V21h-4v-5.5c0-1.31-.03-3-1.83-3-1.83 0-2.11 1.43-2.11 2.9V21h-4V9Z" />
    </svg>
  );
}

function IconPhone() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.49 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.4 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.69a16 16 0 0 0 6 6l.76-.76a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

export function Footer() {
  const pathname = usePathname();
  if (pathname?.startsWith('/admin')) return null;
  return (
    <footer
      className="border-t mt-24"
      style={{ borderColor: 'var(--border)' }}
    >
      <div className="mx-auto max-w-content px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5">
              <LogoMark size={32} />
              <span className="font-mono text-sm font-semibold">
                <span className="text-text">alejandro</span>
                <span className="text-accent">.software</span>
              </span>
            </div>
            <p className="mt-4 text-sm text-text-muted leading-relaxed max-w-xs">
              AI engineer. I design and ship production AI systems.
            </p>
            <p className="mt-3 text-xs text-text-caption" style={{ lineHeight: 1.6 }}>
              Agents · RAG · Evals · LLM-integrated products
            </p>
            <p className="mt-6 font-mono text-xs text-text-caption">
              © {new Date().getFullYear()} Alejandro Morales
            </p>
          </div>

          {/* Navigate */}
          <div>
            <h3
              className="font-mono text-xs font-bold text-text-muted"
              style={{ letterSpacing: '0.12em' }}
            >
              NAVIGATE
            </h3>
            <ul className="mt-4">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="inline-flex items-center text-sm text-text-secondary hover:text-text transition-colors duration-200"
                    style={{ minHeight: 44 }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h3
              className="font-mono text-xs font-bold text-text-muted"
              style={{ letterSpacing: '0.12em' }}
            >
              CONNECT
            </h3>
            <ul className="mt-4 space-y-2.5">
              <li>
                <a
                  href="sms:8455448757"
                  className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-accent transition-colors"
                  style={{ minHeight: 44 }}
                >
                  <IconPhone />
                  Text (845) 544-8757
                </a>
              </li>
              <li>
                <a
                  href="tel:8455448757"
                  className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text transition-colors"
                  style={{ minHeight: 44 }}
                >
                  <IconPhone />
                  Call (845) 544-8757
                </a>
              </li>
              <li>
                <Link
                  href="/book"
                  className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-accent transition-colors"
                  style={{ minHeight: 44 }}
                >
                  <IconCalendar />
                  Book 30 min
                </Link>
              </li>
              <li>
                <CopyEmailButton
                  email="hello@alejandro.software"
                  className="text-sm text-text-secondary hover:text-text transition-colors"
                >
                  <span
                    className="inline-flex items-center gap-2"
                    style={{ minHeight: 44 }}
                  >
                    <IconMail />
                    hello@alejandro.software
                  </span>
                </CopyEmailButton>
              </li>
              <li>
                <a
                  href="https://github.com/alemorale7777"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text transition-colors"
                  style={{ minHeight: 44 }}
                >
                  <IconGitHub />
                  GitHub
                </a>
              </li>
              {/* No LinkedIn link: the owner has no profile yet, and the URL
                  that used to be here almost certainly belongs to a different
                  Alejandro Morales. Sending a recruiter to a stranger is worse
                  than showing nothing. Restore this once the real profile
                  exists; IconLinkedIn is kept ready below. */}
            </ul>
          </div>
        </div>

        <div
          className="mt-12 pt-6 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="flex flex-col gap-1">
            <p className="font-mono text-xs text-text-caption">
              Built and shipped from a single keyboard.
            </p>
            {/* T12.6 — dev trail: a quiet pointer to the homepage scene for the
                curious. The console greeting in PondSceneV3 is the other half. */}
            <Link
              href="/pond-v3"
              rel="nofollow"
              className="font-mono text-[11px] text-text-caption hover:text-accent transition-colors"
              style={{ opacity: 0.7 }}
            >
              🌙 the koi pond, up close →
            </Link>
          </div>
          <div className="flex items-center gap-5">
            <Link
              href="/privacy"
              className="font-mono text-xs text-text-caption hover:text-text-secondary transition-colors"
              style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center' }}
            >
              Privacy
            </Link>
            <Link
              href="/terms"
              className="font-mono text-xs text-text-caption hover:text-text-secondary transition-colors"
              style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center' }}
            >
              Terms
            </Link>
            <Link
              href="/admin/login"
              className="font-mono text-xs text-text-caption hover:text-text-secondary transition-colors"
              style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center' }}
            >
              Sign in
            </Link>
            <p className="font-mono text-xs text-text-caption">Beaverton, OR</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
